import { Component, type ErrorInfo, type ReactNode } from 'react';

import { ErrorFallback } from './ErrorFallback';
import { reportError } from './reportError';

/**
 * Root error boundary. Wraps the authenticated app tree.
 *
 * Contract:
 *   - Catches render-time exceptions so the user never sees a blank page.
 *   - Reports errors through `reportError()` — PHI-safe (no props / state /
 *     response bodies; just errorName + message + componentStack).
 *   - Offers recovery: retry (clear the local error state), reload, sign out.
 *   - Resets automatically when the boundary is unmounted — use `resetKeys`
 *     on the caller's side if you need per-route resets later.
 */

interface State {
  error: Error | null;
  componentStack: string | null;
}

interface Props {
  readonly children: ReactNode;
}

export class RootErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    const componentStack = info.componentStack ?? null;
    this.setState({ error, componentStack });
    reportError({ source: 'error_boundary', error, componentStack });
  }

  reset = (): void => {
    this.setState({ error: null, componentStack: null });
  };

  override render(): ReactNode {
    if (this.state.error !== null) {
      return <ErrorFallback error={this.state.error} onReset={this.reset} />;
    }
    return this.props.children;
  }
}
